// Activity tab — paginated activity log + a small KPI block.
//
// Mirrors web/scripts/admin.js's loadActivity() + initActivityGrid().
// Same columns, same action filter dropdown, same ag-grid rendering.

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import type { ActivityRow, OrgScope } from "../../api/admin";
import { adminApi } from "../../api/admin";
import {
  Chart,
  ErrorBlock,
  KpiCard,
  LoadingBlock,
  SourceBadge,
  formatDateTime,
  formatNumber,
  horizontalBarOption,
} from "./_shared";

const ACTIONS = [
  "",
  "query_submitted",
  "query_completed",
  "query_error",
  "feedback_submitted",
  "insight_saved",
  "insight_updated",
  "dashboard_created",
  "report_generated",
  "report_emailed",
  "share_created",
  "thread_created",
  "org_switched",
  "login",
];

interface Props {
  bearer: string;
  scope: OrgScope;
}

export function ActivityTab({ bearer, scope }: Props) {
  const [action, setAction] = useState<string>("");
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [overviewError, setOverviewError] = useState<Error | null>(null);
  const [overviewKpis, setOverviewKpis] = useState<{
    events_today?: number;
    events_7d?: number;
    unique_users_7d?: number;
    top_actions_7d?: Array<{ action: string; count: number }>;
  } | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  // Headline KPIs come from /admin/overview's activity_kpis block.
  useEffect(() => {
    let cancelled = false;
    setOverviewError(null);
    adminApi
      .overview(bearer, scope)
      .then((d) => {
        if (!cancelled) setOverviewKpis(d.activity_kpis ?? {});
      })
      .catch((e: Error) => {
        if (!cancelled) setOverviewError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope]);

  // Activity grid — page 1, 200 rows (the legacy uses the same defaults).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminApi
      .activity(bearer, { ...scope, action: action || undefined, page: 1, page_size: 200 })
      .then((d) => {
        if (cancelled) return;
        setRows(d.items || []);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope, action]);

  const colDefs = useMemo<ColDef<ActivityRow>[]>(
    () => [
      {
        field: "created_at",
        headerName: "Time",
        width: 180,
        sort: "desc",
        valueFormatter: (p) => formatDateTime(p.value as string),
      },
      { field: "user_email", headerName: "User", flex: 1 },
      { field: "organization", headerName: "Org", width: 140 },
      {
        field: "source",
        headerName: "Source",
        width: 110,
        cellRenderer: (p: { value: string }) => <SourceBadge source={p.value} />,
      },
      { field: "action", headerName: "Action", width: 200 },
      {
        field: "trace_id",
        headerName: "Trace",
        flex: 1,
        valueFormatter: (p) => (p.value ? String(p.value) : ""),
        cellStyle: { fontFamily: "var(--font-mono, monospace)", fontSize: 11 },
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiCard label="Events (today)" value={formatNumber(overviewKpis?.events_today)} />
        <KpiCard label="Events (7d)" value={formatNumber(overviewKpis?.events_7d)} />
        <KpiCard label="Unique Users (7d)" value={formatNumber(overviewKpis?.unique_users_7d)} />
        <KpiCard label="Top Actions (7d)" value="">
          {overviewKpis?.top_actions_7d && overviewKpis.top_actions_7d.length > 0 ? (
            <Chart
              height={60}
              option={horizontalBarOption(
                overviewKpis.top_actions_7d.map((a) => ({ label: a.action, value: a.count })),
              )}
            />
          ) : (
            <div className="text-xs text-neutral-400">—</div>
          )}
        </KpiCard>
      </div>
      {overviewError && <ErrorBlock error={overviewError} />}

      <div className="flex items-end gap-3">
        <div>
          <label className="block text-xs text-neutral-500 mb-1" htmlFor="activity-action-filter">
            Action
          </label>
          <select
            id="activity-action-filter"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="h-8 px-2 pr-7 text-sm rounded-md border border-line bg-white text-neutral-800 hover:bg-chip focus:outline-none focus:ring-1 focus:ring-brand"
          >
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a || "All actions"}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <ErrorBlock error={error} />}
      {loading ? (
        <LoadingBlock />
      ) : (
        <div className="ag-theme-quartz" style={{ width: "100%", height: 540 }}>
          <AgGridReact
            rowData={rows}
            columnDefs={colDefs}
            defaultColDef={{ sortable: true, filter: true, resizable: true }}
            pagination={true}
            paginationPageSize={50}
            paginationPageSizeSelector={[25, 50, 100]}
          />
        </div>
      )}
    </div>
  );
}
